import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { Spinner } from "@/components/Spinner";
import { ChevronLeft, Receipt, ArrowDownLeft, ArrowUpRight } from "lucide-react";

export default function PurchaseHistory() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  usePageMeta(t("transaction_history"));
  const [data, setData] = useState(undefined);

  useEffect(() => {
    api.get("/billing/history?limit=200").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  const fmtDate = (s) => { try { return new Date(s).toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" }); } catch { return s; } };
  const rows = data?.rows || [];
  const bal = data?.balance || {};

  return (
    <div className="mx-auto max-w-2xl space-y-4" data-testid="purchase-history-screen">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/app/account")} data-testid="purchases-back" className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/60 hover:bg-accent"><ChevronLeft className="h-5 w-5" /></button>
        <div>
          <h1 className="font-display text-2xl text-foreground">{t("transaction_history")}</h1>
          <p className="text-sm text-muted-foreground">{t("purchases_refunds_and_coach_earnings")}</p>
        </div>
      </div>

      {data === undefined ? <Spinner label={t("common.loading")} /> : (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-2xl border border-border bg-card p-3">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t("purchases_balance")}</p>
              <p className="mt-1 font-display text-xl text-foreground">{bal.credits ?? 0}</p>
            </div>
            {(bal.coach_earnings_cleared_cents || bal.coach_earnings_pending_cents) ? (
              <>
                <div className="rounded-2xl border border-border bg-card p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t("coach_earnings_cashable")}</p>
                  <p className="mt-1 font-display text-xl text-foreground">{((bal.coach_earnings_cleared_cents || 0) / 100).toFixed(2)} €</p>
                </div>
                <div className="rounded-2xl border border-border bg-card p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t("coach_earnings_held")}</p>
                  <p className="mt-1 font-display text-xl text-foreground">{((bal.coach_earnings_pending_cents || 0) / 100).toFixed(2)} €</p>
                </div>
              </>
            ) : null}
          </div>

          {rows.length === 0 ? (
            <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-border p-10 text-center" data-testid="purchases-empty">
              <Receipt className="h-7 w-7 text-muted-foreground" />
              <p className="text-sm italic text-muted-foreground">{t("no_transactions_yet")}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {rows.map((r, i) => {
                const amt = Number(r.amount ?? r.credits ?? r.delta ?? 0);
                const credit = amt >= 0;
                return (
                  <div key={r.id || r.transaction_id || i} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3" data-testid={`txn-${i}`}>
                    <span className={`flex h-9 w-9 items-center justify-center rounded-full ${credit ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>
                      {credit ? <ArrowDownLeft className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground">{r.description || r.reason || r.kind || t("transaction_history")}</p>
                      <p className="text-xs text-muted-foreground">{fmtDate(r.created_at || r.at)}</p>
                    </div>
                    {amt !== 0 ? <span className={`shrink-0 text-sm font-semibold ${credit ? "text-emerald-600" : "text-rose-600"}`}>{credit ? "+" : ""}{amt} {t("credits_2")}</span> : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
